import { SsoStatus, SsoType } from '@tribeplatform/gql-client/types'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { useAuthToken, useSsos } from '@tribeplatform/react-sdk/hooks'
import { Divider } from '@tribeplatform/react-ui-kit/Divider'

import {
  AuthOperationType,
  isFunctioningSsoOption,
  isVisibleSsoOption,
} from '../../../server/middlewares/ssos/utils/sso.utils.js'
import { SsoAuthForm } from './Forms/SsoAuthForm.js'

type SsoOptionsProps = {
  operationType: AuthOperationType
  redirectPath: string
  invitation?: {
    linkId: string
    token: string
  }
}
export const SsoOptions = ({
  invitation,
  operationType,
  redirectPath,
}: SsoOptionsProps) => {
  const { $t } = useI18n()
  const {
    data: { network },
  } = useAuthToken()
  const { data: enabledSsos = [] } = useSsos({
    variables: { status: SsoStatus.enable },
  })

  const canAuthenticateWithForm = !network.hideDefaultAuthenticationForm

  const functioningSsoOptions = enabledSsos.filter(
    ({ type, authorizationUrl, signUpUrl, tokenUrl }) =>
      isFunctioningSsoOption({ type, authorizationUrl, signUpUrl, tokenUrl }),
  )
  const visibleSsoOptions = functioningSsoOptions
    .filter(({ type, buttonText }) => isVisibleSsoOption({ type, buttonText }))
    .map(sso => {
      // Assert the JWT button text if missing. This is a special case.
      if (sso.type === SsoType.jwt && !sso.buttonText) {
        sso.buttonText = $t({
          defaultMessage: 'Login with JWT',
          id: 'Auth.Ssos.JwtLogin',
        })
      }
      return sso
    })

  if (visibleSsoOptions.length === 0) {
    return null
  }

  return (
    <>
      {canAuthenticateWithForm && (
        <Divider spacing="wide" className="mt-5 mb-2">
          <T defaultMessage="Or continue with" id="Auth.Ssos.DividerText" />
        </Divider>
      )}
      <div className="grid grid-cols-1 gap-3">
        {visibleSsoOptions.map(ssoProvider => (
          <SsoAuthForm
            key={ssoProvider.type}
            ssoProvider={ssoProvider}
            redirectPath={redirectPath}
            operationType={operationType}
            invitation={invitation}
          />
        ))}
      </div>
    </>
  )
}
